import { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router";
import { useLTIP } from "@hooks/useLTIP";
import Grid from "./PSUGrid";
import { useStockPrice } from "@hooks/useStockPrice";
import CustomButton from "@components/CustomButton";
import NumberBoxWrapper from "@/components/NumberBoxWrapper";
import NoDataMessage from "@/components/NoDataMessage";
import withDisclaimer from "@/hoc/withDisclaimer";
import HouseholdClientTabs from "@/components/HouseholdClientTabs";
import StockPriceTooltip from "@/components/StockPriceTooltip";
import { LTIPType } from "@/types/enums";
import useHeaderChange from "@/hooks/useHeaderChange";
import { SarSkeleton } from "../SAR/SarSkeleton";
import useSelectedClient from "@/hooks/useSelectedClient";
import useDefaultSelectedClient from "@/hooks/useDefaultSelectedClient";
import { checkClientsBirthdates, getOwnerName } from "@/helper/helper";
import { NoContactBirthDateMessage } from "../HouseholdContact";

function PerformanceStockAwards() {
  const { id } = useParams();
  const { household } = useHeaderChange(id as string, "Performance Stock Awards");

  const [stockPrice, setStockPrice] = useState(0);
  const {
    data,
    isSuccess: psuLoaded,
    isLoading: isPsuLoading,
    isRefetching: isPsuRefetching,
    isError: psuError,
  } = useLTIP(id as string, LTIPType.PSU, null, household);

  const { selectedClient, setSelectedClient } = useSelectedClient(household, LTIPType.PSU);
  useDefaultSelectedClient(household, data, selectedClient, setSelectedClient, LTIPType.PSU);

  const psuForClient = useMemo(() => {
    if (psuLoaded && selectedClient) {
      const clientPsu = data?.data?.find((item) => item.ownerId === selectedClient);

      return { ...clientPsu, price: stockPrice };
    } else return undefined;
  }, [psuLoaded, selectedClient, data?.data, stockPrice]);

  const { data: stockInfo, isSuccess: stockInfoFetched } = useStockPrice(psuForClient?.ticker);

  useEffect(() => {
    if (stockInfoFetched) setStockPrice(stockInfo?.data?.value ?? 0);
  }, [selectedClient, stockInfo?.data?.value, stockInfoFetched]);

  if (isPsuLoading || isPsuRefetching) return <SarSkeleton />;

  if (checkClientsBirthdates(household)) {
    return <NoContactBirthDateMessage household={household} />;
  }

  return (
    <div className={`flex flex-col text-neutral-100 ${psuForClient ? "gap-4" : ""}`}>
      <div id="psu-combined-values" className="w-1/2" />
      <div className="py-2" />
      <h1 className="text-lg font-medium">
        {psuForClient?.name ?? ""}{" "}
        {!data?.bothClientsHaveData && !household?.isSingle
          ? `for ${getOwnerName(household, selectedClient)}`
          : ""}{" "}
      </h1>
      <div className="flex items-center justify-between">
        {household?.secondaryContact && data?.bothClientsHaveData ? (
          <HouseholdClientTabs
            household={household}
            selectedClientExternalCode={selectedClient}
            setSelectedClientExternalCode={setSelectedClient}
          />
        ) : null}
        <div className="z-40 flex gap-6">
          {stockInfo?.data?.value !== stockPrice && stockInfoFetched ? (
            <CustomButton
              text="Reset"
              variant="primary"
              onClickHandler={() => {
                setStockPrice(stockInfo?.data?.value ?? 0);
              }}
            />
          ) : null}
          {stockInfoFetched && (stockInfo.hasError || stockInfo?.hasWarning) ? (
            <StockPriceTooltip
              ticker={psuForClient?.ticker}
              timestamp={stockInfo?.data?.timestamp}
            />
          ) : null}
          {!psuForClient?.ticker && psuLoaded ? (
            <div className="w-[148px] text-center">No Ticker</div>
          ) : !psuForClient?.externalCode ? null : (
            <NumberBoxWrapper
              format={"$ #,##0.00"}
              stylingMode="outlined"
              labelMode="floating"
              width={148}
              label={`${psuForClient?.ticker} Stock Price`}
              value={stockPrice ?? 0}
              onValueChange={(val) => {
                setStockPrice(val);
              }}
            />
          )}
        </div>
      </div>
      {psuError ? "Error loading PSU data." : null}
      {!psuForClient?.externalCode && psuLoaded && !isPsuLoading ? (
        <NoDataMessage />
      ) : (
        <Grid psu={psuForClient} stockPrice={stockPrice} />
      )}
    </div>
  );
}

const psuWithDisclaimer = withDisclaimer(PerformanceStockAwards);

export default psuWithDisclaimer;
